import React from 'react';
import { Handle, Position, NodeResizer } from '@xyflow/react';
import { NODE_STYLES, NODE_TYPE_CONFIGS } from '../constants';
import CommonHandle from './CommonHandle';
import DeleteButton from './DeleteButton';
import NodeContent from './NodeContent';
import AiStaffNodeBadge from './AiStaffNodeBadge';

const nodeColorMap = Object.fromEntries(
  NODE_TYPE_CONFIGS.map(c => [c.type, c.iconColor])
);

const RESIZER_HANDLE_STYLE = { width: 8, height: 8, borderRadius: 2 };
const RESIZER_LINE_STYLE = { borderWidth: 1 };
const RESIZER_MIN_HEIGHT = 50;

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const getNodeStyle = (reactFlowType, selected, data) => {
  const color = data.iconColor || nodeColorMap[data.type] || '#1890ff';

  switch (reactFlowType) {
    case 'input':
      return {
        ...NODE_STYLES.input,
        ...(selected ? NODE_STYLES.inputSelected : {})
      };
    case 'output':
      return {
        ...NODE_STYLES.output,
        ...(selected ? NODE_STYLES.outputSelected : {})
      };
    default:
      return {
        ...NODE_STYLES.default,
        border: `1.5px solid ${color}`,
        ...(selected
          ? { background: hexToRgba(color, 0.06), boxShadow: `0 0 8px ${hexToRgba(color, 0.35)}` }
          : NODE_STYLES.unselected)
      };
  }
};

// 創建節點類型對象
export const createNodeTypesObj = (onDelete, t, onHandleTypeChange) => {
  return {
    default: ({ id, data, selected }) => (
      <div
        style={getNodeStyle('default', selected, data)}
        className="custom-node"
      >
        <NodeResizer
          isVisible={selected}
          minWidth={140}
          minHeight={RESIZER_MIN_HEIGHT}
          color={data.iconColor || nodeColorMap[data.type] || '#1890ff'}
          handleStyle={RESIZER_HANDLE_STYLE}
          lineStyle={RESIZER_LINE_STYLE}
        />
        <CommonHandle
          type="target"
          position={Position.Top}
          id="top-target"
          style={{ top: -7, left: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        <CommonHandle
          type="target"
          position={Position.Left}
          id="left-target"
          style={{ left: -7, top: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        <CommonHandle
          type="source"
          position={Position.Right}
          id="right-source"
          style={{ right: -7, top: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        
        <NodeContent data={data} />
        
        <CommonHandle
          type="source"
          position={Position.Bottom}
          id="bottom-source"
          style={{ bottom: -7, left: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        
        {data.aiStaffAvatarUrl && (
          <AiStaffNodeBadge avatarUrl={data.aiStaffAvatarUrl} name={data.aiStaffName} jobTitle={data.aiStaffJobTitle} />
        )}
        {selected && data.type !== 'start' && (
          <DeleteButton onClick={() => onDelete(id)} t={t} />
        )}
      </div>
    ),
    input: ({ id, data, selected }) => (
      <div
        style={getNodeStyle('input', selected, data)}
        className="custom-node"
      >
        <NodeResizer
          isVisible={selected}
          minWidth={100}
          minHeight={RESIZER_MIN_HEIGHT}
          color={data.iconColor || nodeColorMap[data.type] || '#52c41a'}
          handleStyle={RESIZER_HANDLE_STYLE}
          lineStyle={RESIZER_LINE_STYLE}
        />
        <CommonHandle
          type="target"
          position={Position.Left}
          id="left-target"
          style={{ left: -7, top: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        
        <NodeContent data={data} />
        
        <CommonHandle
          type="source"
          position={Position.Right}
          id="right-source"
          style={{ right: -7, top: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        <CommonHandle
          type="source"
          position={Position.Bottom}
          id="bottom-source"
          style={{ bottom: -7, left: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        {data.aiStaffAvatarUrl && (
          <AiStaffNodeBadge avatarUrl={data.aiStaffAvatarUrl} name={data.aiStaffName} jobTitle={data.aiStaffJobTitle} />
        )}
      </div>
    ),
    output: ({ id, data, selected }) => (
      <div
        style={getNodeStyle('output', selected, data)}
        className="custom-node"
      >
        <NodeResizer
          isVisible={selected}
          minWidth={100}
          minHeight={RESIZER_MIN_HEIGHT}
          color={data.iconColor || nodeColorMap[data.type] || '#faad14'}
          handleStyle={RESIZER_HANDLE_STYLE}
          lineStyle={RESIZER_LINE_STYLE}
        />
        <CommonHandle
          type="target"
          position={Position.Top}
          id="top-target"
          style={{ top: -7, left: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        <CommonHandle
          type="target"
          position={Position.Left}
          id="left-target"
          style={{ left: -7, top: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        
        <NodeContent data={data} />
        
        <CommonHandle
          type="source"
          position={Position.Right}
          id="right-source"
          style={{ right: -7, top: '50%' }}
          t={t}
          onTypeChange={onHandleTypeChange}
          nodeId={id}
        />
        {data.aiStaffAvatarUrl && (
          <AiStaffNodeBadge avatarUrl={data.aiStaffAvatarUrl} name={data.aiStaffName} jobTitle={data.aiStaffJobTitle} />
        )}
        {selected && (
          <DeleteButton onClick={() => onDelete(id)} t={t} />
        )}
      </div>
    ),
  };
};
